import { moveLocation } from "@/apis/inventoryv2";
import { LocationSelect } from "@/components/Selector";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { InventoryLocationItem, LocationLockEnum, WLocation } from "@/types/inventoryV2";
import { getLocationIdx } from "@/utils/wLocation";
import { Flex, Input, InputNumber, Modal, notification, Space, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";


interface BatchMoveLocationProps {
  open: boolean;
  selectedRows: Array<InventoryLocationItem>;
  onMoveSuccess: () => void;
  onClose: () => void;
  locationId: string;
};

interface MoveData extends InventoryLocationItem {
  moveNum?: number;
}

const BatchMoveLocation: FC<BatchMoveLocationProps> = ({ open, selectedRows, onMoveSuccess, onClose, locationId }) => {

  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [moveDatas, setMoveDatas] = useState<Array<MoveData>>([]);

  const [selectLocationOpen, setSelectLocationOpen] = useState(false);
  const [targetLocation, setTargetLocation] = useState<WLocation | null>(null);

  const columns: ColumnsType<MoveData> = [
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 300,
      fixed: 'left',
      render: (_, record) => <SText text={record.lotItem?.lot?.lot || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'supplierBatchNo',
      width: 150,
      render: (_, record) => <SText text={record.lotItem?.lot?.thdLot || ''} />
    },

    {
      title: <SText text={t('product.code')} />,
      key: 'productCode',
      width: 120,
      render: (_, record) => <SText text={record.lotItem?.product?.code || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.name')} />,
      key: 'productName',
      width: 150,
      render: (_, record) => <SText text={record.lotItem?.product?.name || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.brand1')} />,
      key: 'model',
      width: 120,
      render: (_, record) => <SText text={record.lotItem?.product?.model || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.uom')} />,
      key: 'uom',
      width: 80,
      render: (_, record) => <SText text={record.lotItem?.product?.uom || ''} style={{ width: "100%" }} />
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 80,
      fixed: 'right',
      render: (value) => value || 0
    },
    {
      title: t('warehouse.moveCount'),
      dataIndex: 'moveNum',
      key: 'moveNum',
      width: 120,
      fixed: 'right',
      render: (value, record) => <InputNumber changeOnWheel min={0} max={record.count} value={record.moveNum} onChange={val => {
        setMoveDatas(last => [
          ...last.map(data => ({
            ...data,
            moveNum: data.id === record.id ? (val || 0) : data.moveNum
          }))

        ])
      }} />
    },
    // {
    //   title: t('warehouse.preIn'),
    //   dataIndex: 'preIn',
    //   key: 'preIn',
    //   width: 100,
    //   fixed: 'right',
    //   render: (value) => value || 0
    // },
    // {
    //   title: t('warehouse.preOut'),
    //   dataIndex: 'preOut',
    //   key: 'preOut',
    //   width: 100,
    //   fixed: 'right',
    //   render: (value) => value || 0
    // },

  ];

  useEffect(() => {
    if (selectedRows.length > 0) {
      setMoveDatas(selectedRows.map(row => ({
        ...row,
        moveNum: 0
      })))
    }
  }, [selectedRows])

  const handleLocationSelect = (location: WLocation) => {
    if (location.id === locationId) return notification.error({ placement: 'top', message: t('warehouse.moveFail'), description: t('warehouse.moveCurTip') })
    if (location.lockState === LocationLockEnum.LOCKED) return notification.error({ placement: 'top', message: t('warehouse.moveFail'), description: t('warehouse.locationLocked') })
    setTargetLocation(location);
    setSelectLocationOpen(false);
  };

  const handleOk = () => {
    let moves = moveDatas.filter(data => !!data.moveNum)
    if (!!!targetLocation) return notification.error({
      message: t('warehouse.pleaseSelectLocation')
    })
    if (moves.length === 0) return notification.error({
      message: t('warehouse.leastOneMove')
    })
    setLoading(true)
    moveLocation(locationId, targetLocation?.id ?? '', moves.map(data => ({
      lotStr: data?.lotItem?.lot?.lot || '',
      productCode: data?.lotItem?.product?.code || '',
      barcodes: [],
      count: data.moveNum!
    })))
      .then(() => {
        notification.success({
          message: t('warehouse.moveSuccess')
        })
        onMoveSuccess();
      })
      .catch((err: any) => {
        if (err?.code === 73007 || err?.code === 73008) {
          if (Array.isArray(err?.extra)) {
            let errCodes = moveDatas
              .filter(item => err.extra.includes(item.lotItem.productId) || err.extra.includes(item.lotItem?.product?.code))
              .map(item => item.lotItem?.product?.code ?? '')
              .join(', ')
            notification.error({
              placement: 'top',
              message: t('apiError.error31'),
              description: errCodes,
            });
          } else notification.error({
            placement: 'top',
            message: t('warehouse.moveFail'),
            description: t('warehouse.insufficientInventory')
          })
        } else notification.error({
          message: t('warehouse.moveFail'),
          description: err
        })
      })
      .finally(() => setLoading(false))

  }


  return (
    <Modal width={1000} onCancel={onClose} destroyOnClose okButtonProps={{ loading }} open={open} onOk={handleOk}>
      <Flex vertical gap={16} style={{ height: '60vh' }}>
        <Space style={{ width: '100%' }} direction="vertical" size="small">
          <Typography.Title level={4}>{t('warehouse.targetLocation')}</Typography.Title>
          <Flex>
            <span>{t('warehouse.selectLocation')}：</span>
            <Input value={getLocationIdx(targetLocation)} readOnly onClick={() => setSelectLocationOpen(true)} size='small' style={{ flex: 1 }} />
          </Flex>

        </Space>

        <Typography.Title level={4}>{t('purchase.productInfo')}</Typography.Title>

        <Flex style={{ width: "100%", flex: 1 }} gap={16} align="center">
          <STableLayout
            headerChildren={null}
            tableLayoutRef={tableRef}
            total={selectedRows.length}
            dataSource={selectedRows}
            handleChange={() => { }}
            columns={columns as ColumnsType<AnyObject>}
            scrollX={1250}
            pagination={{ pageSize: 5 }}
          />
        </Flex>
      </Flex>
      <LocationSelect
        open={selectLocationOpen}
        onCancel={() => setSelectLocationOpen(false)}
        onChange={handleLocationSelect}
        value={targetLocation?.id}
      />
    </Modal>
  )
};
export default BatchMoveLocation;